import * as React from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import ForgotPasswordForm from 'bundles/authentication/shared/ForgotPasswordForm';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
  hideBackToLink?: boolean;
};

export type State = {
  isSent: boolean;
  requestMagicLink: boolean;
};

const api = API('', { type: 'rest' });
const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '24px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '32px 16px',
  },
  description: {
    textAlign: 'center',
  },
  navigation: {
    margin: '12px auto 0',
    textAlign: 'center',
    display: 'flex',
  },
  navigationSent: {
    marginTop: '150px',
  },
  navigationIcon: {
    marginRight: '0.6rem',
  },
  section: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'OpenSans',
    fontSize: '24px',
    lineHeight: '30px',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '34px',
      lineHeight: '42px',
    },
  },
});

class ForgotPassword extends React.Component<Props, State> {
  state = {
    isSent: false,
    requestMagicLink: false,
  };

  navigateToLogin = () => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  resetPassword = ({ email }: { email: string }) => {
    api.post('api/password/v1/requestReset', { data: { email } });

    this.setState({ isSent: true });
  };

  sendMagicLink = ({ email }: { email: string }) => {
    const { magicLinkRedirect } = this.props;
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });

    this.navigateToMagicLink(email);
  };

  requestMagicLink = () => {
    this.setState(() => ({ requestMagicLink: true }));
  };

  navigateToMagicLink = (email: string) => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email });
  };

  render() {
    const { className, hideBackToLink = false } = this.props;
    const { isSent, requestMagicLink } = this.state;

    return (
      <section {...css(className, styles.container)} aria-live="polite">
        <header {...css(styles.section)}>
          <h1 {...css(styles.title)}>{isSent ? _t('Email has been sent') : _t('Forgot password')}</h1>
        </header>
        <p {...css(styles.description, styles.section)}>
          {isSent ? (
            <TrackedDiv trackingName="reset_request_sent" withVisibilityTracking={true}>
              {_t(
                "Please check your email inbox for a password recovery link. Don't forget to check your spam folder."
              )}
            </TrackedDiv>
          ) : (
            _t("Enter the email address you use on Coursera. We'll send you a link to reset your password.")
          )}
        </p>
        {!isSent && (
          <ForgotPasswordForm
            onSubmit={requestMagicLink ? this.sendMagicLink : this.resetPassword}
            onRequestMagicLink={this.requestMagicLink}
            submitCTALabel={_t('Reset Password')}
            submitCTATrackingName="reset_page_button"
          />
        )}
        {!hideBackToLink && (
          <p {...css(styles.navigation, isSent && styles.navigationSent, styles.section)}>
            <FormattedMessage
              message={_t('Back to {loginLink}')}
              loginLink={<LinkButton onClick={this.navigateToLogin}>{_t('Log in')}</LinkButton>}
            />
          </p>
        )}
      </section>
    );
  }
}

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'reset_password_page',
  },
}))(ForgotPassword);
